import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { ASSIGNMENT_LABEL } from '../../lib/format';
import { Button, Dialog, ErrorNotice, Field, Input, SectionNote, useToast } from '../../components/ui';

export interface EndableAssignment {
  assignmentId: string;
  accountId: string;
  accountName: string;
  fullName: string;
  assignmentType: string;
}

/**
 * §6.3 — take someone off an account.
 *
 * Ending is a closing date, never a delete: the row stays so that "who held
 * this account in March" remains answerable, and §45 records the change. The
 * primary CSM is deliberately not endable here — the API refuses it, because an
 * account with no primary CSM is not a state the model allows. Reassigning the
 * primary on the account closes the old record as part of that change.
 */
export function EndAssignmentDialog({
  assignment,
  onClose,
}: {
  assignment: EndableAssignment;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [endsAt, setEndsAt] = useState('');

  const end = useMutation({
    mutationFn: () =>
      api.patch(`/accounts/assignments/${assignment.assignmentId}/end`, {
        endsAt: endsAt || undefined,
      }),
    onSuccess: () => {
      toast('Assignment ended');
      void queryClient.invalidateQueries({ queryKey: ['admin-ownership'] });
      void queryClient.invalidateQueries({ queryKey: ['account-summary', assignment.accountId] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  const label = ASSIGNMENT_LABEL[assignment.assignmentType] ?? assignment.assignmentType;

  return (
    <Dialog
      open
      onClose={onClose}
      title="End this assignment"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={end.isPending} onClick={() => end.mutate()}>
            {end.isPending ? 'Ending…' : 'End assignment'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {end.error && <ErrorNotice error={end.error} />}

        <p className="text-sm text-ink">
          <span className="font-medium">{assignment.fullName}</span> will stop being the{' '}
          <span className="font-medium">{label.toLowerCase()}</span> on{' '}
          <span className="font-medium">{assignment.accountName}</span>.
        </p>

        <Field label="Ends" hint="Defaults to now. A future date schedules the end.">
          <Input type="date" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} />
        </Field>

        <SectionNote>
          The assignment is closed, not deleted — the history of who held this account stays intact,
          and the change is recorded in the audit trail.
        </SectionNote>
      </div>
    </Dialog>
  );
}
